import React, { useEffect, useMemo } from 'react'

import { Box, Chip, Divider, Typography } from '@mui/material'

import { getCountriesAndCities } from '../../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import type { CountryAndCities } from '../../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.types'
import { RootState } from '../../../../redux/store/store.types'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { getCountryByCode, getCountryFlagEmoji } from '../../../../shared/functions/Places/places.functions'
import { getStatusColor } from '../../../../shared/functions/Status/status.functions'
import { formatMoney } from '../../../../shared/functions/String/string.functions'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import styles from './FulfillmentOrderDetails.styles'
import { FulfillmentOrderDetailsProps } from './FulfillmentOrderDetails.types'

const resolveCityName = (
  cityCodeOrName: string,
  countryCode: string,
  countriesAndCities: CountryAndCities[]
): string => {
  const country = countriesAndCities.find(
    (countryItem) => countryItem.iataCountryCode?.toUpperCase() === countryCode?.toUpperCase()
  )

  if (!country) return cityCodeOrName

  const city = country.cities?.find(
    (cityItem) => cityItem.iataCityCode?.toUpperCase() === cityCodeOrName?.toUpperCase()
  )

  return city?.cityName ?? cityCodeOrName
}

const formatCentsToDollars = (cents: number, currency: string): string => {
  return formatMoney(cents / 100, currency)
}

const formatDateDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '—'

  return date(dateString).format(dateTimeFormats.date.medium) || '—'
}

const DetailField: React.FC<{ label: string; value: string | React.ReactNode }> = ({ label, value }) => (
  <Box sx={styles.fieldContainer}>
    <Typography sx={styles.fieldLabelText}>{label}</Typography>

    {typeof value === 'string' ? <Typography sx={styles.fieldValueText}>{value || '—'}</Typography> : value}
  </Box>
)

const FulfillmentOrderDetails: React.FC<FulfillmentOrderDetailsProps> = (props) => {
  const { orderDetails } = props

  const dispatch = useAppDispatch()
  const countriesAndCitiesData = useAppSelector((state: RootState) => state.getCountriesAndCities?.success?.data)
  const countriesAndCities = useMemo(
    () => (countriesAndCitiesData as CountryAndCities[]) || [],
    [countriesAndCitiesData]
  )

  useEffect(() => {
    if (countriesAndCities.length === 0) {
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, countriesAndCities.length])

  const totalDeclaredValueCents = orderDetails.items.reduce(
    (accumulator, item) => accumulator + item.declaredValueCents,
    0
  )

  const deliveryFeeCents = orderDetails.totalFeeCents
  const currency = orderDetails.currency

  const renderOrderSummary = () => (
    <Box sx={styles.sectionContainer}>
      <Typography sx={styles.sectionTitleText}>Order Summary</Typography>

      <Box sx={styles.summaryTotalsContainer}>
        <Box sx={styles.fieldRow}>
          <DetailField label="Items" value={String(orderDetails.items.length)} />
          <DetailField label="Total Declared Value" value={formatCentsToDollars(totalDeclaredValueCents, currency)} />
        </Box>
      </Box>
    </Box>
  )

  const renderRecipientDetails = () => {
    const { recipient } = orderDetails

    const countryDisplayName = getCountryByCode(recipient.country)?.name ?? recipient.country

    const cityDisplayName = resolveCityName(recipient.city, recipient.country, countriesAndCities)

    return (
      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionTitleText}>Recipient Details</Typography>

        <Box sx={styles.fieldsGrid}>
          <Box sx={styles.fieldRow}>
            <DetailField label="Name" value={recipient.name} />
            <DetailField label="Email" value={recipient.email} />
          </Box>

          <Box sx={styles.fieldRow}>
            <DetailField label="Phone" value={recipient.phone} />
            <DetailField
              label="Country"
              value={
                <Box component="span" sx={styles.countryWithFlag}>
                  {getCountryFlagEmoji(recipient.country)}
                  <Typography component="span" sx={styles.fieldValueText}>
                    {countryDisplayName}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={styles.fieldRow}>
            <DetailField label="Address" value={recipient.streetAddress} />
            <DetailField label="City / State" value={`${cityDisplayName}, ${recipient.stateProvince}`} />
          </Box>

          {recipient.deliveryInstructions && (
            <Box sx={styles.fieldRow}>
              <DetailField label="Delivery Instructions" value={recipient.deliveryInstructions} />
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  const renderPaymentSection = (
    feeCents: number,
    paymentStatus: import('./FulfillmentOrderDetails.types').PaymentSectionProps['paymentStatus'],
    paymentDate: string | null
  ) => {
    const statusColor = paymentStatus ? getStatusColor(paymentStatus.name) : 'default'
    const statusLabel = paymentStatus?.displayName || '-'

    return (
      <Box sx={styles.sectionContainer}>
        <Box sx={styles.paymentFieldsRow}>
          <DetailField label="Total Fees" value={formatCentsToDollars(feeCents, currency)} />

          <DetailField label="Payment Status" value={<Chip label={statusLabel} size="small" color={statusColor} />} />

          <DetailField label="Payment Date" value={formatDateDisplay(paymentDate)} />
        </Box>
      </Box>
    )
  }

  const renderPaymentDetails = () => {
    return (
      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionTitleText}>Payment Details</Typography>

        <Box sx={styles.paymentSectionsContainer}>
          {renderPaymentSection(deliveryFeeCents, orderDetails.platformPaymentStatus, orderDetails.createdAt)}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      {renderOrderSummary()}
      <Divider sx={styles.majorSectionDivider} />
      {renderRecipientDetails()}
      <Divider sx={styles.majorSectionDivider} />
      {renderPaymentDetails()}
    </Box>
  )
}

export default FulfillmentOrderDetails
